import React, { useContext, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Dropdown, Icon } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/User';
import { API, isAdmin, showSuccess } from '../helpers';

const menuItems = [
  { icon: '🔗', label: '渠道', path: '/channel', admin: true },
  { icon: '🛍️', label: '模型广场', path: '/products' },
  { icon: '🧪', label: '操练场', path: '/playground-channel', admin: true },
  { icon: '🔑', label: '令牌', path: '/token' },
  { icon: '💬', label: '试聊', path: '/playground' },
  { icon: '💰', label: '兑换', path: '/redemption', admin: true },
  { icon: '💵', label: '充值', path: '/topup' },
  { icon: '👤', label: '用户', path: '/user', admin: true },
  { icon: '📊', label: '总览', path: '/dashboard' },
  { icon: '📋', label: '日志', path: '/log' },
  { icon: '⚙️', label: '设置', path: '/setting' },
  { icon: 'ℹ️', label: '关于', path: '/about' }
];

const authPaths = ['/login', '/register', '/reset', '/user/reset'];

const SIDEBAR_KEY = 'sidebar_collapsed';

const AppLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [userState, userDispatch] = useContext(UserContext);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_KEY) === 'true');

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      localStorage.setItem(SIDEBAR_KEY, String(!prev));
      return !prev;
    });
  };

  const isAuthPage =
    authPaths.some((path) => location.pathname.startsWith(path)) ||
    location.pathname.startsWith('/oauth/');

  const handleLogout = async () => {
    await API.get('/api/user/logout');
    showSuccess('注销成功!');
    userDispatch({ type: 'logout' });
    localStorage.removeItem('user');
    navigate('/login');
  };

  if (isAuthPage) {
    return <>{children}</>;
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <aside
        className="shrink-0 border-r border-gray-200 bg-white flex flex-col"
        style={{
          width: collapsed ? 64 : 256,
          transition: 'width 0.2s ease-in-out',
          overflow: 'hidden',
        }}
      >
        <div className="flex h-16 items-center gap-3 border-b border-gray-200 px-3" style={{ minHeight: 64 }}>
          <img
            src="/MindHubLogo.png"
            alt="Mind Hub Logo"
            className="h-10 w-10 rounded-lg shrink-0"
            style={{ objectFit: 'cover', objectPosition: 'center' }}
          />
          {!collapsed && <span className="text-lg font-semibold text-gray-900 whitespace-nowrap">Mind Hub</span>}
        </div>
        <nav className="flex-1 p-2" style={{ overflowX: 'hidden' }}>
          {menuItems
            .filter((item) => !item.admin || isAdmin())
            .map((item) => {
              const isActive = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  title={collapsed ? item.label : undefined}
                  className={`mb-1 flex items-center rounded-lg text-sm no-underline ${
                    isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  style={{
                    padding: collapsed ? '8px 0' : '8px 12px',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    gap: collapsed ? 0 : 12,
                  }}
                >
                  <span className="shrink-0 text-base" style={{ width: 24, textAlign: 'center' }}>{item.icon}</span>
                  {!collapsed && <span className="whitespace-nowrap">{item.label}</span>}
                </Link>
              );
            })}
        </nav>
        <button
          onClick={toggleCollapsed}
          className="flex items-center border-t border-gray-200 bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          style={{
            padding: collapsed ? '12px 0' : '12px',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: collapsed ? 0 : 8,
            cursor: 'pointer',
            border: 'none',
            borderTop: '1px solid #e5e7eb',
            width: '100%',
            fontSize: 14,
          }}
        >
          <span style={{ display: 'inline-block', transition: 'transform 0.2s', transform: collapsed ? 'rotate(180deg)' : 'none' }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          {!collapsed && <span>收起侧栏</span>}
        </button>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 items-center justify-end border-b border-gray-200 bg-white px-6">
          <div className="flex items-center gap-4">
            <Dropdown
              inline
              options={[
                { key: 'zh', text: '中文', value: 'zh' },
                { key: 'en', text: 'English', value: 'en' }
              ]}
              value={i18n.language}
              onChange={(_, { value }) => i18n.changeLanguage(value)}
            />
            {userState.user ? (
              <Dropdown text={userState.user.username} pointing className="link item">
                <Dropdown.Menu>
                  <Dropdown.Item onClick={handleLogout}>退出登录</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <Link to="/login" className="text-gray-700 no-underline">
                <Icon name="user outline" /> 登录
              </Link>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>
    </div>
  );
};

export default AppLayout;
